"use client";
import React, { useCallback, useEffect, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { useBrowse } from "../../lib/hooks";
import type { Location } from "@/lib/api/types";

const GOOGLE_MAPS_LIBRARIES: "marker"[] = ["marker"];
const CENTER = { lat: 34.075, lng: -118.28 };
const OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: "cooperative",
  clickableIcons: false,
};
const CONTAINER_STYLE = { width: "100%", height: "100%" };

interface GoogleMapProps {
  children?: React.ReactNode;
  locations: Pick<Location, "lat" | "lng">[];
}

const Map = ({ children, locations }: GoogleMapProps) => {
  const { isMapView } = useBrowse();
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_API_KEY as string,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const onLoad = useCallback(
    (instance: google.maps.Map) => setMap(instance),
    [],
  );
  const boundsKey = locations
    .filter(
      ({ lat, lng }) =>
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        (lat !== 0 || lng !== 0),
    )
    .map(({ lat, lng }) => `${lat}:${lng}`)
    .join("|");
  useEffect(() => {
    if (!map || !boundsKey) return;
    const bounds = new google.maps.LatLngBounds();
    boundsKey.split("|").forEach((point) => {
      const [lat, lng] = point.split(":").map(Number);
      bounds.extend({ lat, lng });
    });
    let lastSize = "";
    const fit = () => {
      const element = map.getDiv();
      const size = `${element.clientWidth}:${element.clientHeight}`;
      if (size === lastSize || !element.clientWidth || !element.clientHeight)
        return;
      lastSize = size;
      const horizontalPadding = element.clientWidth < 600 ? 40 : 100;
      map.fitBounds(bounds, {
        top: 60,
        bottom: 80,
        left: horizontalPadding,
        right: horizontalPadding,
      });
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(map.getDiv());
    return () => observer.disconnect();
  }, [map, boundsKey]);

  return (
    <div id="Map" className={isMapView ? "is-mapview" : "is-listview"}>
      {loadError ? (
        <p className="location-map-status" role="status">
          The map couldn’t load. Use List to explore our locations.
        </p>
      ) : isLoaded ? (
        <GoogleMap
          mapContainerStyle={CONTAINER_STYLE}
          center={CENTER}
          zoom={10}
          onLoad={onLoad}
          options={OPTIONS}
        >
          {children}
        </GoogleMap>
      ) : (
        <p className="location-map-status" role="status">
          Loading map…
        </p>
      )}
    </div>
  );
};

export default Map;
